/**
 * useAIRefMutations
 *
 * Mutation handlers for AI reference slots:
 * set, upload+set, transfer+set, clear.
 */

import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  getAIReferenceImages,
  setAIReferenceImage,
  deleteAIReferenceImage,
  uploadPendingImage,
  type AIRefSlot,
} from '@/lib/api/products';
import { transferFromGalleryToProduct } from '@/lib/api/product-images';

interface MutationDeps {
  productId: string;
  setSlots: (slots: AIRefSlot[]) => void;
  setSaving: (slot: number | null) => void;
  closePicker: () => void;
}

export function useAIRefMutations({ productId, setSlots, setSaving, closePicker }: MutationDeps) {
  const reloadSlots = async () => {
    const res = await getAIReferenceImages(productId);
    if (res.success) setSlots(res.slots);
  };

  const setReference = async (slot: number, assetId: string, role?: string) => {
    setSaving(slot);
    try {
      const res = await setAIReferenceImage(productId, { slot, asset_id: assetId, role });
      if (res.success) {
        await reloadSlots();
        closePicker();
        toast.success(i18n.t('toast.success.referenceImageSet'));
      }
    } catch (err) {
      console.error('Error setting AI reference:', err);
      toast.error('Fehler beim Setzen des Referenzbildes');
    } finally {
      setSaving(null);
    }
  };

  const uploadAndSet = async (slot: number, file: File, role?: string) => {
    setSaving(slot);
    try {
      const res = await uploadPendingImage(productId, file);
      if (res.success && res.asset_id) {
        await setReference(slot, res.asset_id, role);
      }
    } catch (err) {
      console.error('Error uploading and setting reference:', err);
      toast.error('Fehler beim Hochladen');
    } finally {
      setSaving(null);
    }
  };

  const transferAndSet = async (slot: number, imageId: string, role?: string) => {
    setSaving(slot);
    try {
      const res = await transferFromGalleryToProduct(imageId, productId, {
        targetType: 'ai_reference',
        slot,
      });
      if (res.success) {
        if (role && res.asset_id) {
          await setAIReferenceImage(productId, { slot, asset_id: res.asset_id, role });
        }
        await reloadSlots();
        closePicker();
        toast.success(i18n.t('toast.success.imageTransferredAndSet'));
      }
    } catch (err) {
      console.error('Error transferring and setting reference:', err);
      toast.error('Fehler beim Uebertragen');
    } finally {
      setSaving(null);
    }
  };

  const clearSlot = async (slot: number) => {
    if (!confirm('KI-Referenzbild aus diesem Slot entfernen?')) return;
    setSaving(slot);
    try {
      const res = await deleteAIReferenceImage(productId, slot);
      if (res.success) {
        await reloadSlots();
        toast.success(i18n.t('toast.success.referenceImageRemoved'));
      }
    } catch (err) {
      console.error('Error clearing AI reference:', err);
      toast.error('Fehler beim Entfernen');
    } finally {
      setSaving(null);
    }
  };

  return { setReference, uploadAndSet, transferAndSet, clearSlot };
}
